import { Controller } from "@hotwired/stimulus"
import ToastController from "@controllers/toast_controller"

export default class extends Controller {
  static targets = ["seedIntents", "manualToggle", "manualFields", "questionBlocks", "applyButton"]
  static values = { updateUrl: String, seedIntents: String, questionBlocks: String }

  connect() {
    this.toast = new ToastController()
    this.prefill()
    if (this.hasManualToggleTarget) {
      this.toggleManual()
    }
  }

  toggleManual() {
    if (!this.hasManualFieldsTarget) return

    if (this.manualToggleTarget.checked) {
      this.manualFieldsTarget.classList.remove("hidden")
    } else {
      this.manualFieldsTarget.classList.add("hidden")
    }
  }

  async apply(event) {
    event.preventDefault()

    if (!this.hasUpdateUrlValue) {
      this.notifyError("Seed update endpoint is missing")
      return
    }

    const seedIntents = this.hasSeedIntentsTarget ? this.seedIntentsTarget.value : ""
    const payload = { seed_intents: seedIntents }

    if (this.hasManualToggleTarget && this.manualToggleTarget.checked) {
      const rawBlocks = this.hasQuestionBlocksTarget ? this.questionBlocksTarget.value : ""
      if (!rawBlocks.trim()) {
        this.notifyError("Please provide question blocks")
        return
      }

      const trimmed = rawBlocks.trim()
      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
          JSON.parse(trimmed)
        } catch (error) {
          this.notifyError("Question blocks must be valid JSON")
          return
        }
      }

      payload.question_blocks = rawBlocks
    } else {
      payload.regenerate = true
    }

    this.setLoading(true)

    try {
      const response = await fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        const data = await response.json()
        this.refreshEditorState(data)
        this.notifySuccess("Seed updated")
        this.closeModal()
      } else {
        const error = await response.json()
        this.notifyError(error.error || "Failed to update seed")
      }
    } catch (error) {
      console.error("Seed update error:", error)
      this.notifyError("Failed to update seed")
    } finally {
      this.setLoading(false)
    }
  }

  prefill() {
    if (this.hasSeedIntentsTarget && this.hasSeedIntentsValue) {
      this.seedIntentsTarget.value = this.seedIntentsValue || ""
    }

    if (this.hasQuestionBlocksTarget && this.hasQuestionBlocksValue) {
      this.questionBlocksTarget.value = this.formatBlocks(this.questionBlocksValue)
    }
  }

  formatBlocks(raw) {
    if (!raw) return ""

    try {
      const parsed = JSON.parse(raw)
      const blocks = Array.isArray(parsed) ? parsed : (parsed?.question_blocks || [])
      if (Array.isArray(blocks) && blocks.length > 0) {
        return this.blocksToMarkdown(blocks)
      }
      return JSON.stringify(parsed, null, 2)
    } catch (error) {
      return raw
    }
  }

  refreshEditorState(data) {
    if (this.hasSeedIntentsTarget && Object.prototype.hasOwnProperty.call(data, "seed_intents")) {
      this.seedIntentsTarget.value = data.seed_intents || ""
    }

    if (this.hasQuestionBlocksTarget && Object.prototype.hasOwnProperty.call(data, "question_blocks")) {
      this.questionBlocksTarget.value = this.blocksToMarkdown(data.question_blocks || [])
    }
  }

  blocksToMarkdown(blocks) {
    if (!Array.isArray(blocks) || blocks.length === 0) return ""

    const lines = []
    blocks.forEach(block => {
      const title = (block?.title || "Conversation Seed").toString().trim()
      lines.push(`## ${title}`)
      const questions = Array.isArray(block?.questions) ? block.questions : []
      questions.forEach(question => {
        const text = question?.toString().trim()
        if (text) {
          lines.push(`- ${text}`)
        }
      })
      lines.push("")
    })

    return lines.join("\n").trim()
  }

  setLoading(isLoading) {
    if (!this.hasApplyButtonTarget) return

    this.applyButtonTarget.disabled = isLoading
    if (isLoading) {
      this.applyButtonTarget.dataset.originalLabel = this.applyButtonTarget.innerText
      this.applyButtonTarget.innerText = "Updating..."
    } else if (this.applyButtonTarget.dataset.originalLabel) {
      this.applyButtonTarget.innerText = this.applyButtonTarget.dataset.originalLabel
      delete this.applyButtonTarget.dataset.originalLabel
    }
  }

  closeModal() {
    const modal = this.element.querySelector("[data-controller~='modal']")
    if (!modal) return

    const modalController = this.application.getControllerForElementAndIdentifier(modal, "modal")
    if (modalController) {
      modalController.close()
    }
  }

  notifySuccess(message) {
    if (window.toastController) {
      window.toastController.success(message)
      return
    }

    this.toast.success(message)
  }

  notifyError(message) {
    if (window.toastController) {
      window.toastController.error(message)
      return
    }

    this.toast.error(message)
  }
}
